import { init, thirdPartyErrorFilterIntegration } from '@sentry/react'
import { isSandbox, isProduction } from 'helpers/environment'

import { beforeSend } from './infrastructure/sentry/beforeSend'

export const getEnvironment = () => {
  if (isProduction()) {
    return 'production'
  }

  if (isSandbox()) {
    return 'sandbox'
  }

  return 'development'
}

export const setupSentry = () => {
  init({
    dsn: 'https://9e160401fde44d47bf8f8229fa5958ec@o4508601944047696.ingest.de.sentry.io/4508602000801876',
    environment: getEnvironment(),
    beforeSend,
    integrations: [
      thirdPartyErrorFilterIntegration({
        // Specify the application keys that is specified in the Sentry bundler plugin
        filterKeys: [process.env.REACT_APP_SENTRY_APP_FILTER!],
        behaviour: 'apply-tag-if-exclusively-contains-third-party-frames',
      }),
    ],
    ignoreErrors: [
      // When axios receives xmlHttpRequest.onerror event declaring that low level network
      // error occurred on user device (i.e. http status can't be even calculated and equals to 0),
      // it then creates an instance of simple `Error` with this message.
      // @see https://github.com/axios/axios/blob/9a78465a9268dcd360d7663de686709a68560d3d/lib/adapters/xhr.js#L84
      // Since this is user environment related issue we can't do anything about it.
      'Network Error',
      // Ingore js errors caused by Lottie and Google Translate
      // @see https://github.com/airbnb/lottie-web/issues/1134#issuecomment-565808141
      // @see https://medium.com/@amir.harel/a-b-target-classname-indexof-is-not-a-function-at-least-not-mine-8e52f7be64ca
      'TypeError: a[b].target.className.indexOf is not a function',
      // Ignore google translate+react from reporting
      // @see https://github.com/facebook/react/issues/11538#issuecomment-390386520
      // @see https://bugs.chromium.org/p/chromium/issues/detail?id=872770
      `NotFoundError: Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.`,
      'NotFoundError: The object can not be found here.',
      // https://stackoverflow.com/a/50387233
      // https://forum.sentry.io/t/resizeobserver-loop-limit-exceeded/8402/5
      'ResizeObserver loop limit exceeded',
      // Same as above but mostly for Firefox and some other browsers.
      // https://www.w3.org/TR/resize-observer/#deliver-resize-error
      'ResizeObserver loop completed with undelivered notifications.',
      // Ignore errors happened inside safari-extensions
      /safari-extension/gi,
      // Ignore errors caused by Microsoft Outlook SafeLink crawlers
      // https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/7
      // https://revolut.atlassian.net/browse/PBACC-5809
      'Non-Error promise rejection captured with value: Object Not Found Matching Id:',
      // HTML5 video — https://revolut.atlassian.net/browse/WB-64235?focusedCommentId=20705660
      'AbortError: The play() request was interrupted by a call to pause().',
      // HTML5 video — Unsupported browsers/user denied permission
      'The play method is not allowed by the user agent',
      // Edge mobile browser error in video player
      // https://techcommunity.microsoft.com/discussions/edgeinsiderdiscussions/error-cant-find-variable-logmutedmessage-on-edge-mobile-/3912307
      // https://answers.microsoft.com/en-us/microsoftedge/forum/all/logmutedmessage-accessed-incorrectly-and-throwing/ebfb660e-94ca-4af6-b3d8-8d5efc98d439
      `Can't find variable: logMutedMessage`,
    ],
    denyUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      // https://bugs.webkit.org/show_bug.cgi?id=246010
      /.*@webkit-masked-url:.*/,
      // GTM injected scripts
      /bat\.bing\.com\/p\/insights/i,
      // Branch.io related errors
      /cdn\.branch\.io\/branch-latest/i,
      // Translate google com related errors
      /translate\.google\.com\/translate_a/i,
      // Google tag manager
      /googletagmanager\.com\/gtag/i,
    ],
  })
}
